<script setup lang="ts">
import { mdiCartOutline, mdiLockOutline } from '@mdi/js'

interface Props {
  productId: string
  title: string
  priceId: string
  amount: number
  saleTitle?: string
  billingCycle?: string
  description?: string
  isUnlocked?: boolean
  isProcessing?: boolean
  isPurchased?: boolean
  isNew?: boolean
  purchaseDisabled?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['click', 'submit'])

// const { isSceneUnlocked } = useSceneAccess()
// const isUnlocked = computed(() => isSceneUnlocked(props.sceneId))

const formattedAmount = computed(() => {
  if (props.amount === 0) {
    return '無料'
  }
  if (props.billingCycle === 'monthly') {
    return `${props.amount.toLocaleString()}`
  }
  if (props.billingCycle === 'annually') {
    return `${props.amount.toLocaleString()}`
  }
  return `${props.amount.toLocaleString()}`
})

const formattedUnit = computed(() => {
  if (props.amount === 0) {
    return ''
  }
  if (props.billingCycle === 'monthly') {
    return '円／月'
  }
  if (props.billingCycle === 'annually') {
    return '円／年'
  }
  return '円'
})

// #721: 商品アイコンは productId → 静的アセットの共通ヘルパーに集約（mobile と共有）。
const codexSrc = computed(() => productIconFor(props.productId))

const onClick = () => {
  emit('click', props.productId)
}

const onSubmit = () => {
  if (props.purchaseDisabled) return
  emit('submit', props.productId)
}
</script>

<template>
  <v-card
    :class="['price-card', props.isNew ? 'glow-border-cycle' : '']"
    :ripple="false"
    :data-price-id="props.productId"
    @click="onClick"
  >
    <div class="price-card-inner d-flex flex-column ga-1">
      <!-- Image and overlay text -->
      <div class="flex-grow-0">
        <v-img
          :lazy-src="codexSrc"
          :src="codexSrc"
          aspect-ratio="1"
          cover
          class="codex-image"
        >
          <div class="overlay-text d-flex flex-column ga-4">
            <div class="flex-grow-1">
              <p>{{ title }}</p>
            </div>
            <div class="flex-grow-1">
              <p class="price-text">
                {{ formattedAmount }}<span class="price-text-unit">&nbsp;{{ formattedUnit }}</span>
              </p>
              <p v-if="saleTitle">
                {{ saleTitle }}
              </p>
            </div>
          </div>
        </v-img>
      </div>
      <!-- Description text -->
      <div class="flex-grow-1 description-text">
        <p>{{ description }}</p>
      </div>
    </div>
    <v-card-actions>
      <v-btn
        size="large"
        variant="elevated"
        color="primary"
        :class="{ 'btn-purchased': isPurchased }"
        :disabled="purchaseDisabled"
        @click="onSubmit"
      >
        <template v-if="isProcessing">
          <v-progress-circular
            indeterminate
            size="small"
          />&nbsp;処理中
        </template>
        <template v-else-if="isPurchased">
          <v-icon>{{ mdiCartOutline }}</v-icon>購入済
        </template>
        <template v-else-if="purchaseDisabled">
          <v-icon>{{ mdiLockOutline }}</v-icon>保護者から購入
        </template>
        <template v-else-if="!isUnlocked">
          <v-icon>{{ mdiLockOutline }}</v-icon>ロック
        </template>
        <template v-else>
          <v-icon>{{ mdiCartOutline }}</v-icon>購入する
        </template>
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.price-card {
  width: 100%;
  /* max-width: 200px; */
  aspect-ratio: 7 / 7.2;
  background-color: aliceblue;
  box-shadow: 7px 7px 5px #00000060;
  /* padding: clamp(8.89px, 1.25dvw, 16px); */
  /* padding: clamp(8.89px, 1.25dvw, 16px) clamp(4.44px, 0.625dvw, 8px); */
  padding: max(4.44px, calc(0.625 * var(--u)));
  border-radius: 8px;

  cursor: auto;
  user-select: none;
  position: relative;
  overflow: visible;

  font-family: "Noto Sans JP", sans-serif !important;
}

.price-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  /* flex: 1; */

  overflow: visible;
}

.codex-image {
  width: 100%;
  /* height: 100%; */
  aspect-ratio: 1 / 1;
  object-fit: contain;
  border-radius: 6px;
  /* filter: brightness(30%) sepia(90%); */
}

.overlay-text {
  position: absolute;
  top: 45%;
  left: 50%;
  width: 90%;
  transform: translate(-50%, -50%);
  /* color: white; */
  /* font-size: 2.0vh; */
  /* font-size: clamp(17.78px, 2.5dvw, 32px); */
  font-size: max(13.33px, calc(1.875 * var(--u)));
  font-weight: bold;
  /* text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.7); */
  /* pointer-events: none; */
  text-align: center;

  color: #000;
  text-shadow:
    1px 1px 0 #fff,
    -1px -1px 0 #fff,
    -1px 1px 0 #fff,
    1px -1px 0 #fff,
    0px 1px 0 #fff,
    0-1px 0 #fff,
    -1px 0 0 #fff,
    1px 0 0 #fff;
}

.price-text {
  /* font-size: 1.8em; */
  font-size: max(17.78px, calc(2.5 * var(--u)));
  font-weight: 900;
  color: orangered;
}

.price-text-unit {
  font-size: max(8.89px, calc(1.25 * var(--u)));
  font-weight: 900;
  color: orangered;
}

.description-text {
  /* font-size: clamp(8.89px, 1.25dvw, 16px); */
  font-size: max(8.33px, calc(1.17 * var(--u)));
  font-weight: 500;
  color: #000;
}

.price-card .v-card-actions {
  padding: 0;
  min-height: 0;
}

.price-card .v-btn {
  width: 100%;
  height: max(28px, calc(3.75 * var(--u)));
  min-height: 0;
  font-size: max(13.33px, calc(1.875 * var(--u)));
}

.price-card .v-btn :deep(.v-icon) {
  font-size: max(13.33px, calc(1.875 * var(--u)));
}

.price-card .v-btn.btn-purchased {
  opacity: 0.6;
}

/* .glow-border-cycle {
  position: relative;
  overflow: hidden;
} */

/* 光る境界 */
.glow-border-cycle::before {
  content: "";
  position: absolute;
  inset: 0;
  padding: 10px;
  border-radius: inherit;

  background: linear-gradient(
    90deg,
    transparent,
    /* rgba(200, 255, 100, 0.9), */
    #FF7F18E0,
    transparent
  );
  background-size: 200% 100%;

  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;

  pointer-events: none;

  animation: border-glow-cycle 3.5s linear infinite;
  opacity: 0;
}

/* 一周 → 消える → 待つ */
@keyframes border-glow-cycle {
  0% {
    background-position: 0% 50%;
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  40% {
    background-position: 200% 50%;
    opacity: 1;
  }

  50% {
    opacity: 0;
  }

  100% {
    background-position: 200% 50%;
    opacity: 0;
  }
}
</style>
